import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { reportsApi, type ReportCatalogEntry, type ReportType } from '../../api/reports.api.js';
import { StatusBadge } from '../../components/StatusBadge.js';
import { APP_LOCALE } from '../../utils/locale.js';

/**
 * Regulatory reports (DEV-83). The form says what it needs and why a button is
 * disabled; a report that cannot be produced says why, in the list, instead of
 * hanging or failing silently; and every job shows who asked, by name.
 */

function periodProblem(startDate: string, endDate: string): string {
  if (startDate === '' || endDate === '') return 'Choose a start and an end date.';
  if (startDate > endDate) return 'The end date must be on or after the start date.';
  return '';
}

export function RegulatoryReportsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<ReportCatalogEntry | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState('');
  const [lastOutcome, setLastOutcome] = useState<{ status: string; name: string } | null>(null);

  /**
   * Pull the report through the authenticated client and hand it to the browser
   * as an object URL. A plain link could not carry the Bearer token.
   */
  const downloadReport = async (jobId: string, reportType: string): Promise<void> => {
    setDownloading(jobId);
    setDownloadError('');
    try {
      const blob = await reportsApi.download(jobId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportType.toLowerCase()}-${jobId.slice(0, 8)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (error: unknown) {
      setDownloadError(error instanceof Error ? error.message : 'Could not download that report.');
    } finally {
      setDownloading(null);
    }
  };

  const catalogQuery = useQuery({ queryKey: ['report-catalog'], queryFn: () => reportsApi.listCatalog() });
  const jobsQuery = useQuery({ queryKey: ['report-jobs'], queryFn: () => reportsApi.listJobs({ limit: 20 }) });
  const names = new Map((catalogQuery.data?.reports ?? []).map((entry) => [entry.type, entry.name]));

  const generateMutation = useMutation({
    mutationFn: (reportType: ReportType) =>
      reportsApi.generate(reportType, {
        startDate,
        endDate,
        format: 'CSV',
        ...(jurisdiction !== '' ? { jurisdiction } : {}),
      }),
    onSuccess: async (result, reportType) => {
      await queryClient.invalidateQueries({ queryKey: ['report-jobs'] });
      setLastOutcome({ status: result.status, name: names.get(reportType) ?? reportType });
      setSelected(null);
    },
  });

  const openDialog = (entry: ReportCatalogEntry): void => {
    generateMutation.reset();
    setSelected(entry);
    setJurisdiction('');
  };

  const problem = periodProblem(startDate, endDate);

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Regulatory Reports
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each report covers a period you choose and is produced as a CSV file straight away. Finished
        reports stay in the list below for download.
      </Typography>

      {catalogQuery.isError ? (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={() => void catalogQuery.refetch()}>Retry</Button>}>
          The list of reports could not be loaded.
        </Alert>
      ) : null}
      {lastOutcome !== null ? (
        <Alert severity={lastOutcome.status === 'COMPLETED' ? 'success' : 'error'} role="status" sx={{ mb: 2 }} onClose={() => setLastOutcome(null)}>
          {lastOutcome.status === 'COMPLETED'
            ? `${lastOutcome.name} is ready to download below.`
            : `${lastOutcome.name} could not be produced. The reason is shown against it below.`}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {catalogQuery.data?.reports.map((entry) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={entry.type}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }} aria-label={entry.name}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6">{entry.name}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {entry.description}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Chip size="small" label={entry.cadence.toLowerCase()} />
                  <Chip size="small" label="CSV" variant="outlined" />
                </Stack>
                {!entry.available ? (
                  <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1 }}>
                    Not available yet: {entry.unavailableReason}
                  </Typography>
                ) : null}
              </CardContent>
              <CardActions>
                <Button onClick={() => openDialog(entry)} disabled={!entry.available}>
                  Generate
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card>
        <CardContent>
          {downloadError !== '' ? <Alert severity="error" sx={{ mb: 2 }}>{downloadError}</Alert> : null}
          <Typography variant="h6" gutterBottom>
            Recent reports
          </Typography>
          {jobsQuery.isError ? (
            <Alert severity="error" action={<Button onClick={() => void jobsQuery.refetch()}>Retry</Button>}>
              Recent reports could not be loaded.
            </Alert>
          ) : jobsQuery.data?.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No reports have been produced yet.</Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" aria-label="Recent reports">
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Report</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Requested by</TableCell>
                    <TableCell>Requested at</TableCell>
                    <TableCell>Result</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobsQuery.data?.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell><StatusBadge kind="report" value={job.status} /></TableCell>
                      <TableCell>
                        {names.get(job.reportType) ?? job.reportType}
                        {job.jurisdiction !== null ? (
                          <Typography variant="caption" color="text.secondary" display="block">Jurisdiction {job.jurisdiction}</Typography>
                        ) : null}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{job.startDate} → {job.endDate}</TableCell>
                      <TableCell>{job.requestedByName ?? job.requestedBy}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(job.requestedAt).toLocaleString(APP_LOCALE)}</TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        {job.status === 'COMPLETED' ? (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Button size="small" onClick={() => void downloadReport(job.id, job.reportType)} disabled={downloading === job.id}>
                              {downloading === job.id ? 'Preparing…' : 'Download CSV'}
                            </Button>
                            <Typography variant="caption" color="text.secondary">
                              {job.rowCount === 0 ? 'No records in this period' : `${String(job.rowCount ?? 0)} rows`}
                            </Typography>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="error">
                            {job.errorMessage ?? 'Could not be produced.'} Reference {job.id.slice(0, 8)}.
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog open={selected !== null} onClose={() => setSelected(null)} fullWidth maxWidth="sm" aria-labelledby="generate-report-title">
        <DialogTitle id="generate-report-title">Generate {selected?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Required: a start and an end date. The period includes both days. The report is a CSV file.
            </Typography>
            <TextField label="Start date" type="date" value={startDate} required
              onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField label="End date" type="date" value={endDate} required
              onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }}
              error={startDate !== '' && endDate !== '' && problem !== ''}
              helperText={startDate !== '' && endDate !== '' && problem !== '' ? problem : 'On or after the start date.'} />
            {selected?.jurisdictionFilter === true ? (
              <TextField select label="Jurisdiction (optional)" value={jurisdiction}
                onChange={(e) => setJurisdiction(e.target.value)}
                helperText="Leave on All to report on every jurisdiction.">
                <MenuItem value="">All jurisdictions</MenuItem>
                {(catalogQuery.data?.jurisdictions ?? []).map((code) => <MenuItem key={code} value={code}>{code}</MenuItem>)}
              </TextField>
            ) : null}
            {generateMutation.isError ? (
              <Alert severity="error">
                {generateMutation.error instanceof Error ? generateMutation.error.message : 'The report could not be requested.'}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          {problem !== '' ? <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', ml: 2 }}>{problem}</Typography> : null}
          <Button onClick={() => setSelected(null)}>Cancel</Button>
          <Button variant="contained" disabled={problem !== '' || generateMutation.isPending}
            onClick={() => { if (selected !== null) generateMutation.mutate(selected.type); }}>
            {generateMutation.isPending ? 'Producing…' : 'Generate report'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
